import React from 'react'
import { FaChevronDown, FaChevronUp, FaTrash } from 'react-icons/fa'
import {
  maxConfiguredProductFees,
  ProductFee,
  ProductFeeType,
} from '@oneethos/shared'
import './product-fee-editor.scss'

type ProductFeeEditorProps = {
  fees: ProductFee[]
  productType: string
  onChange: (fees: ProductFee[]) => void
}

const FEE_TYPE_OPTIONS: { value: ProductFeeType, label: string }[] = [
  { value: 'flat', label: 'Flat amount' },
  { value: 'percent', label: 'Percent of loan amount' },
  { value: 'flat_plus_percent', label: 'Flat amount + percent' },
]

function numericDraft(value: string): string {
  const cleaned = value.replace(/[^0-9.]/g, '')
  const firstDot = cleaned.indexOf('.')
  if (firstDot === -1) return cleaned
  const whole = cleaned.slice(0, firstDot)
  const fraction = cleaned.slice(firstDot + 1).replace(/\./g, '')
  return `${whole}.${fraction}`
}

const ProductFeeRow = ({
  fee,
  index,
  feeCount,
  onChange,
  onMove,
  onRemove,
}: {
  fee: ProductFee
  index: number
  feeCount: number
  onChange: (fee: ProductFee) => void
  onMove: (direction: -1 | 1) => void
  onRemove: () => void
}) => {
  const showFlat = fee.type === 'flat' || fee.type === 'flat_plus_percent'
  const showPercent = fee.type === 'percent' || fee.type === 'flat_plus_percent'

  const updateType = (type: ProductFeeType) => {
    const next: ProductFee = { ...fee, type }
    if (type === 'flat') delete next.percent
    if (type === 'percent') delete next.flatAmount
    onChange(next)
  }

  return (
    <div className="product-fee-row mb-3">
      <div className="product-fee-field product-fee-label">
          <label>Fee Label</label>
          <input
            type="text"
            className="form-control"
            maxLength={80}
            value={fee.label}
            onChange={event => onChange({ ...fee, label: event.target.value })}
          />
      </div>
      <div className="product-fee-field product-fee-type">
          <label>Fee Type</label>
          <select
            className="form-select form-control"
            value={fee.type}
            onChange={event => updateType(event.target.value as ProductFeeType)}
          >
            {FEE_TYPE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
      </div>
      {showFlat ? (
        <div className="product-fee-field product-fee-amount">
            <label>Flat amount ($)</label>
            <input
              type="text"
              className="form-control"
              value={fee.flatAmount ?? ''}
              onChange={event => onChange({
                ...fee,
                flatAmount: numericDraft(event.target.value) as unknown as number,
              })}
            />
        </div>
      ) : <div className="product-fee-spacer" aria-hidden="true" />}
      {showPercent ? (
        <div className="product-fee-field product-fee-percent">
            <label>Percent (%)</label>
            <input
              type="text"
              className="form-control"
              value={fee.percent ?? ''}
              onChange={event => onChange({
                ...fee,
                percent: numericDraft(event.target.value) as unknown as number,
              })}
            />
        </div>
      ) : <div className="product-fee-spacer" aria-hidden="true" />}
      <div className="product-fee-actions">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm product-fee-action"
            disabled={index === 0}
            onClick={() => onMove(-1)}
            aria-label="Move fee up"
            title="Move fee up"
          >
            <FaChevronUp aria-hidden="true" />
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm product-fee-action"
            disabled={index === feeCount - 1}
            onClick={() => onMove(1)}
            aria-label="Move fee down"
            title="Move fee down"
          >
            <FaChevronDown aria-hidden="true" />
          </button>
          <button
            type="button"
            className="btn btn-outline-danger btn-sm product-fee-action"
            onClick={onRemove}
            aria-label="Remove fee"
            title="Remove fee"
          >
            <FaTrash aria-hidden="true" />
          </button>
      </div>
      <div className="product-fee-field product-fee-fine-print">
        <label className="product-fee-fine-print-label">Fine Print (optional)</label>
        <textarea
          className="form-control product-fee-fine-print-input"
          maxLength={1000}
          rows={2}
          value={fee.finePrint ?? ''}
          placeholder="Displayed as a footnote wherever this fee appears"
          onChange={event => onChange({ ...fee, finePrint: event.target.value })}
        />
      </div>
    </div>
  )
}

const ProductFeeEditor = ({ fees, productType, onChange }: ProductFeeEditorProps) => {
  const maxFees = maxConfiguredProductFees(productType)
  const atLimit = fees.length >= maxFees

  const updateFee = (index: number, fee: ProductFee) => {
    onChange(fees.map((current, feeIndex) => feeIndex === index ? fee : current))
  }

  const moveFee = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= fees.length) return
    const next = [...fees]
    const [moved] = next.splice(index, 1)
    next.splice(target, 0, moved)
    onChange(next)
  }

  const removeFee = (index: number) => {
    onChange(fees.filter((_, feeIndex) => feeIndex !== index))
  }

  const addFee = () => {
    if (atLimit) return
    onChange([
      ...fees,
      { label: '', type: 'flat', flatAmount: 0 },
    ])
  }

  return (
    <div>
      {fees.length > maxFees ? (
        <div className="text-danger mb-2">
          This product type allows at most {maxFees} fees.
        </div>
      ) : null}
      {fees.map((fee, index) => (
        <ProductFeeRow
          key={index}
          fee={fee}
          index={index}
          feeCount={fees.length}
          onChange={next => updateFee(index, next)}
          onMove={direction => moveFee(index, direction)}
          onRemove={() => removeFee(index)}
        />
      ))}
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm"
        disabled={atLimit}
        onClick={addFee}
      >
        Add fee
      </button>
    </div>
  )
}

export default ProductFeeEditor
